import { CurrencyPipe } from '@angular/common';
import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MediaComponent } from '@shared/components/media/media.component';
import { ModalService } from '@shared/components/modal-list/modal-list.service';
import { ProductCardModalComponent } from '@b2b/components/product/product-card-modal/product-card-modal.component';
import { TableColumnReorderEvent, TableModule } from 'primeng/table';
import { ProductStockComponent } from '@b2b/components/product/product-stock/product-stock.component';
import { DragScrollDirective } from '@core/directives/table/drag-scroll.directive';
import { FrozenColumnDirective } from '@core/directives/table/frozen-column.directive';
import { ProductImageService } from '@b2b/features/product/core/services/product-image.service';
import { ProductListService } from '@b2b/features/product/core/services/product-list.service';
import { FormattedProduct, Product, PRODUCT_MEDIA_TYPE } from '@b2b/features/product/core/utilities/product.model';
import { ProductsTableService } from '@b2b/features/product/components/products-table/products-table.service';
import { PRODUCT_LIST_FIELD, ProductListColumnConfig } from '@b2b/features/product/components/products-table/products-table.types';
import { ProductConnector } from '@b2b/features/product/core/services/product.connector';
import { CartService } from '@b2b/features/cart/core/cart.service';
import { map, of, Subscription, switchMap, take } from 'rxjs';
import { SkeletonLoadingComponent } from '@shared/components/skeleton-loading/skeleton-loading.component';
import { PostDeliveryRequest } from '@b2b/features/product/core/utilities/delivery.model';
import { TranslateModule } from '@ngx-translate/core';
import { getDarkBrandLogo } from '@shared/helpers/getBrandLogo';

@Component({
  selector: 'cx-products-table',
  templateUrl: './products-table.component.html',
  styleUrl: './products-table.component.scss',
  imports: [
    FormsModule,
    TableModule,
    DragScrollDirective,
    FrozenColumnDirective,
    ProductStockComponent,
    MediaComponent,
    SkeletonLoadingComponent,
    TranslateModule,
  ],
  providers: [CurrencyPipe],
})
export class ProductsTableComponent implements OnInit, OnDestroy {
  private readonly productListService = inject(ProductListService);
  private readonly productsTableService = inject(ProductsTableService);
  private readonly productImageService = inject(ProductImageService);
  private readonly modalService = inject(ModalService);
  private readonly productConnector = inject(ProductConnector);
  private readonly cartService = inject(CartService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly cart = toSignal(this.cartService.getCart(), { initialValue: null });

  private readonly rawProducts = toSignal(this.productListService.take(), {
    initialValue: [],
  });

  private readonly imageNotFound = this.productImageService.getImageNotFound(PRODUCT_MEDIA_TYPE.PRODUCT);

  PRODUCT_LIST_FIELD = PRODUCT_LIST_FIELD;

  columns = this.productsTableService.activeColumns;
  invoiceAmountLabel = this.productsTableService.invoiceAmountLabel;

  quantities = signal<Record<string, number>>(
    Object.fromEntries(
      this.rawProducts()
        .filter(p => p.code !== undefined)
        .map(p => [p.code, 0]),
    ),
  );
  products = signal<FormattedProduct[]>([]);

  alreadyLoadingStock: Record<string, boolean> = {};
  alreadyLoadingPrice: Record<string, boolean> = {};
  alreadyLoadingDeliveryTime: Record<string, boolean> = {};

  sortField = signal<string | null>(null);
  sortOrder = signal<'asc' | 'desc' | null>(null);

  priceSub: Subscription | undefined = undefined;
  stockSub: Subscription | undefined = undefined;

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.priceSub?.unsubscribe();
    this.stockSub?.unsubscribe();
  }

  getBrandLogo = (brand: string) => {
    if (!brand) return '';
    return getDarkBrandLogo(brand);
  };

  onColReorder(event: TableColumnReorderEvent) {
    if (event.dragIndex !== undefined && event.dropIndex !== undefined) {
      this.productsTableService.reorderColumn(event.dragIndex, event.dropIndex);
    }
  }

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = this.imageNotFound.src;
  }

  openProductCardModal(product: FormattedProduct): void {
    this.modalService.create({
      hasCloseButton: true,
      id: 'product-card-modal',
      title: '',
      width: 400,
      component: ProductCardModalComponent,
      data: { product },
    });
  }

  get allLoaded(): boolean {
    const totalProducts = this.products().length;
    return (
      Object.keys(this.alreadyLoadingDeliveryTime).length >= totalProducts &&
      Object.keys(this.alreadyLoadingPrice).length >= totalProducts &&
      Object.keys(this.alreadyLoadingStock).length >= totalProducts
    );
  }

  private readonly numericSortFields = new Set<string>([
    PRODUCT_LIST_FIELD.INVOICE_AMOUNT,
    PRODUCT_LIST_FIELD.FACTORY_PRICE,
    PRODUCT_LIST_FIELD.ST,
    PRODUCT_LIST_FIELD.PMC,
    PRODUCT_LIST_FIELD.DELIVERY_TIME,
    PRODUCT_LIST_FIELD.DISCOUNT,
  ]);

  private parseCurrencyToNumber(value: string): number {
    const cleaned = value.replace(/[R$\s.]/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  }

  onOrderProduct(col: ProductListColumnConfig, event: Event) {
    event.stopPropagation();

    if (!this.allLoaded) return;

    const field = col.field as keyof FormattedProduct;

    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortOrder.set('asc');
    }

    const isNumericField = this.numericSortFields.has(field);

    const sortedProducts = [...this.products()].sort((a, b) => {
      const aValue = a?.[field];
      const bValue = b?.[field];

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        const comparison = aValue === bValue ? 0 : aValue ? -1 : 1;
        return this.sortOrder() === 'asc' ? comparison : -comparison;
      }

      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      let comparison = 0;

      if (isNumericField) {
        const aNum = this.parseCurrencyToNumber(String(aValue));
        const bNum = this.parseCurrencyToNumber(String(bValue));
        comparison = aNum - bNum;
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        comparison = aStr.localeCompare(bStr);
      }

      return this.sortOrder() === 'asc' ? comparison : -comparison;
    });

    this.productListService.setProductList(sortedProducts);
    this.init();
  }

  private fetchData(index: number) {
    const prods = this.rawProducts();
    const currentProduct = prods[index];

    if (!currentProduct?.stock) {
      this.fetchStock(currentProduct, index);
    } else {
      this.setStockAlreadyLoading(currentProduct.code!);
    }

    if (!currentProduct?.price) {
      this.fetchPrice(currentProduct, index);
    } else {
      this.setPriceAlreadyLoading(currentProduct.code!);
      this.setDeliveryTimeAlreadyLoading(currentProduct.code!);
    }
  }

  private setPriceAlreadyLoading(priceId: string) {
    this.alreadyLoadingPrice[priceId] = true;
  }

  private setStockAlreadyLoading(stockId: string) {
    this.alreadyLoadingStock[stockId] = true;
  }

  private setDeliveryTimeAlreadyLoading(deliveryId: string) {
    this.alreadyLoadingDeliveryTime[deliveryId] = true;
  }

  private fetchPrice(product: Product, index: number) {
    this.productConnector
      .getPrice(product.code!)
      .pipe(
        switchMap(price => {
          const prodWithPrice = {
            ...product,
            price: price,
            factoryPrice: price?.data?.price || 0,
            invoiceAmount: price?.data?.finalPrice || 0,
            desconto: price?.data?.discount || 0,
            taxes: price?.data?.st || 0,
          };

          return of(prodWithPrice);
        }),
        take(1),
      )
      .subscribe(prodWithPrice => {
        const prods = this.products();
        const currentProduct = prods[index];
        const withData = {
          ...currentProduct,
          ...prodWithPrice,
        };

        this.formatSpecificProductInsideList(withData, index);
        this.productListService.setSpecificProduct(withData, index);

        this.setDeliveryTimeAlreadyLoading(product.code!);
        this.setPriceAlreadyLoading(product.code!);

        return prodWithPrice;
      });
  }

  private fetchDeliveryTime(product: Product, index: number, deliveryRequest: PostDeliveryRequest) {
    return this.productConnector.getDeliveryTime(deliveryRequest).pipe(
      map(delivery => ({
        ...product,
        ...delivery.data?.data,
      })),
    );
  }

  private fetchStock(product: Product, index: number) {
    this.productConnector
      .getStock(product.code!)
      .pipe(
        map(stock => ({
          ...product,
          stock: stock,
        })),
        take(1),
      )
      .subscribe(productWithStockResult => {
        this.formatSpecificProductInsideList(productWithStockResult, index);
        this.productListService.setSpecificProduct(productWithStockResult, index);

        this.setStockAlreadyLoading(product.code!);

        return productWithStockResult;
      });
  }

  private resolveApontador(): string {
    const query = this.activatedRoute.snapshot.queryParams['query'] || '';

    if (this.router.url.includes('/oferta')) {
      return query.split(':')[0] || '';
    }

    return this.extractApontadorFromQuery(query);
  }

  private extractApontadorFromQuery(query: string): string {
    if (!query) return '';
    const parts = query.split(':');
    const offersIndex = parts.findIndex(p => p === 'offers');
    if (offersIndex !== -1 && parts[offersIndex + 1]) {
      return parts[offersIndex + 1];
    }
    return '';
  }

  private formatProduct(product: Product) {
    const pricing = product.price?.data;

    return {
      ...product,
      inStock: product.stock?.data?.available ?? false,
      corteTime: product.cutoffTime || '',
      deliveryTime: product.deliveryTime || '0',
      invoiceAmount: pricing?.formatedFinalPrice || '',
      oferta: '',
      discount: pricing?.formatedDiscount || '0%',
      factoryPrice: pricing?.formatedPrice || '',
      taxes: '0%',
      st: pricing?.formatedST || '',
      empresa: product.stock?.data?.brand,
      pmc: '',
      isTaxCalculated: true,
    };
  }

  private formatList(prods: Product[]) {
    const mapped = prods.map((product, index) => {
      this.fetchData(index);

      return this.formatProduct(product);
    });

    this.products.set(mapped);
  }

  private formatSpecificProductInsideList(product: Product, index: number) {
    const productList = [...this.products()];

    const formatted = this.formatProduct(product);

    if (formatted.isTaxCalculated !== undefined) {
      this.productsTableService.setInvoiceAmountLabel(formatted.isTaxCalculated);
    }

    productList[index] = formatted;

    this.products.set(productList);
  }

  private init() {
    const prods = this.rawProducts();
    this.formatList(prods);

    if (!prods || prods.length === 0) {
      this.products.set([]);
      return;
    }

    this.stockSub?.unsubscribe();
    this.priceSub?.unsubscribe();

    // Object.keys(this.visibleFlags).forEach(indexStr => {
    //   const index = Number(indexStr);

    // });
  }
}
